<template>
    <div
        class="o-player player"
        @click.capture.prevent="handleClick"
        :key="renderKey"
    >
        <div v-if="isActive" class="o-player__stage">
            <img
                v-if="isScreensaverSingleImage"
                :src="screensaverUrl"
                class="player-img"
                :alt="$t('o-player-screensaver')"
            >
            <iframe
                v-else
                :src="screensaverUrl"
                allow="autoplay"
            />
            <div @click.capture.prevent="handleClick" class="player-overlay"></div>
        </div>
    </div>
</template>

<script>

import {mapGetters, mapActions, mapState, mapMutations} from 'vuex'
import {reportWarning, reportError} from "@/utils/helpers/health-monitoring";

export default {
    name: "OPlayer",
    data: () =>({
        isCooldown: false,
        isLocalContentReady: false,
        isPlayerPlaying: true,
        screensaverUrl: null,
        checkScreensaverInterval: null,
        checkPlayerPlayingTimeout: null,
        cooldownDelayTimeout: null,
        loopCount: 0,
        isOnline: navigator.onLine,
        renderKey: 0,
        cooldownTimer: null,
        exceptionTimer: null,
        canSendException: true,
        canSendPlaybackException: false
    }),
    methods: {
        updateOnlineStatus() {
            this.isOnline = navigator.onLine;
        },
        handleClick() {
            this.$emit('onPlayerClick', true);
        },
        ...mapActions(['checkScreensaverUrl']),
        onWindowMessage(event){
            if(event && event.data === 'loop_end')
            {
                const loopsCount = this.getMapConfigProp('features.screensaver.cooldown_after_loops_count', 5);
                ++this.loopCount > loopsCount && this.playerCooldown();
            }

            if(event && event.data === 'player_loaded')
            {
                this.isPlayerPlaying = true;
                clearTimeout(this.checkPlayerPlayingTimeout);
            }

            this.checkIframeHealth(event)
        },
        playerCooldown(){
            this.loopCount = 0;
            const cooldownDelay = this.getMapConfigProp('features.screensaver.cooldown_delay', 0);
            if(cooldownDelay){
                this.isCooldown = true;
                clearTimeout(this.cooldownDelayTimeout);
                this.cooldownDelayTimeout = setTimeout(() => (this.isCooldown = false), cooldownDelay*1000 );
            }
        },
        async initPlayer(){
            this.screensaverUrl = this.terminalScreensaverUrl;

            if(this.isScreensaverUrlLocal && this.isLocalContentCheckEnabled) {
                this.isLocalContentReady = await this.checkScreensaverUrl(this.screensaverUrl);
                this.checkScreensaverInterval = setInterval(async () => {
                    this.isLocalContentReady = await this.checkScreensaverUrl(this.screensaverUrl);
                }, this.getMapConfigProp('features.screensaver.local_content_check_interval', 60) * 1000);
            }
            else
            {
                this.isLocalContentReady = true;
            }
        },
        checkIframeHealth(event){
            if(event && event.data === 'loop_end') {
                if(this.cooldownTimer) clearTimeout(this.cooldownTimer)

                this.cooldownTimer = setTimeout(() => {
                    ++this.renderKey
                    this.exceptionHandler();
                    this.canSendPlaybackException = true
                }, this.cooldownHealthTime)

                this.playbackExceptionHandler();
            }
        },
        exceptionHandler() {
            if(this.canSendException) {
                const msg = `Signage-player: Not Playing. Iframe src: ${this.screensaverUrl}. In terminal with id ${this.getTerminalId}`;
                reportError(msg);
                this.canSendException = false
                this.exceptionTimer = setTimeout(() => {
                    this.canSendException = true
                }, 3600000)
            }
        },
        playbackExceptionHandler() {
            if(this.canSendPlaybackException) {
                const msg = `Signage-player: Playback recovered. Iframe src: ${this.screensaverUrl}. In terminal with id ${this.getTerminalId}`;
                reportWarning(msg);
                this.canSendPlaybackException = false
            }
        }
    },
    watch:{
        isActive(value){
            if(value && this.shouldCheckPlayerPlaying){
                const playerPlayingDelay = this.getMapConfigProp('features.screensaver.check_player_playing_delay', 2000);
                this.checkPlayerPlayingTimeout = setTimeout(() => {this.isPlayerPlaying = false}, playerPlayingDelay);
            }
        }
    },
    computed:{
        ...mapState({
            isOfflineMap: state => state.dataSourceType === 'LOCALHOST',
            playerUrl: state => state.playerUrl,
        }),
        ...mapGetters([
            't',
            'getMap',
            'getTerminal',
            'getTerminalId',
            'getMapConfigProp'
        ]),
        terminalScreensaverUrl(){
            return this.playerUrl ||
                this.t(this.getTerminal, 'screensaver_url') ||
                this.t(this.getTerminal, 'playlist_url') ||
                this.t(this.getMap, 'screensaver_url') ||
                '';
        },
        shouldCheckPlayerPlaying(){
            return this.getMapConfigProp('features.screensaver.check_player_playing', false);
        },
        isContentReady(){
            return this.isScreensaverUrlLocal ? this.isLocalContentReady : this.isOnline;
        },
        isActive(){
            return this.screensaverUrl && this.isContentReady && this.isPlayerPlaying && !this.isCooldown;
        },
        isScreensaverSingleImage(){
            return (this.screensaverUrl || '').match(/\.(jpg|jpeg|png)$/);
        },
        isScreensaverUrlLocal() {
            const reg = new RegExp(/localhost|127.0.0.|\/signage\//);
            return reg.test(this.screensaverUrl);
        },
        isLocalContentCheckEnabled(){
            return this.getMapConfigProp('features.screensaver.local_content_check', false);
        },
        commonConfigParams() {
            return this.getMapConfigProp('components.OPlayer.params') || {};
        },
        viewConfigParams() {
            return this.getMapConfigProp(`views.${this.view}.OPlayer.params`) || {};
        },
        componentParams() {
            return {
                ...this.commonConfigParams,
                ...this.viewConfigParams
            }
        },
        cooldownHealthTime() {
            return this.componentParams.cooldown_health_time || 300000
        }
    },
    mounted() {
        this.initPlayer();

        window.addEventListener("message", this.onWindowMessage, false);
        window.addEventListener('online',  this.updateOnlineStatus);
        window.addEventListener('offline', this.updateOnlineStatus);
    },
    beforeDestroy() {
        window.removeEventListener("message",this.onWindowMessage);
        window.removeEventListener("online", this.updateOnlineStatus);
        window.removeEventListener("offline",this.updateOnlineStatus);

        clearInterval(this.checkScreensaverInterval);
        clearTimeout(this.checkPlayerPlayingTimeout);
        clearTimeout(this.cooldownDelayTimeout);

        if(this.cooldownTimer) clearTimeout(this.cooldownTimer)
        if(this.exceptionTimer) clearTimeout(this.exceptionTimer)
    },
}
</script>

<style lang="scss">
@import "o-player";
</style>
